import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MapPin, SimpleMap } from '../../maps/simple-map';
import { GOOGLE_REFUSED_NOTE } from '../../maps/basemap';
import { LocationsService, Nearby, NearbyHandTyped, NearbyPlace, NearbyProvider, NearbyUnplaced, SuburbHit } from '../../services/locations.service';

declare const google: any;

/** What the popup is opened with: from the nav (nothing), or from an arrangement's event. */
export interface VenueMapData {
  /** The suburb to start on (name, state, postcode only: never a street). */
  suburb?: { name: string; state: string; postcode?: string | null } | null;
  /** A point to start on (an event venue's stored position). */
  centre?: { lat: number; lng: number; label?: string } | null;
  /** What the venue is for (the event type's purpose). */
  purpose?: string | null;
}

type Card =
  | { kind: 'provider'; key: string; item: NearbyProvider; rank: number }
  | { kind: 'hand'; key: string; item: NearbyHandTyped }
  | { kind: 'place'; key: string; item: NearbyPlace };

const SYDNEY = { lat: -33.8688, lng: 151.2093, label: 'Sydney' };

/**
 * Find a venue (#250; plan §5.1): the places Picaluna knows near a suburb, ranked by how often they've been used for the
 * purpose, then on "View more" the crawl's places. The start point is a suburb's ABS centre or a stored position: no
 * family's address goes to Google from here. The search box looks suburbs up in our own data; where Google is available
 * a second box refines by place, and nothing from it is stored.
 *
 * Crawled names and links are untrusted text: interpolated only, links http(s) only (the server checks too), pins drawn
 * as text. Responses are brought back into NgZone and redrawn explicitly (the app coalesces change detection).
 */
@Component({
  selector: 'app-venue-map',
  templateUrl: './venue-map.component.html',
  styleUrls: ['./venue-map.component.scss'],
})
export class VenueMapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapEl') mapEl?: ElementRef<HTMLDivElement>;
  @ViewChild('googleInput') googleInput?: ElementRef<HTMLInputElement>;

  readonly purposes = [
    { value: 'all', label: 'Any purpose' },
    { value: 'funeral-service', label: 'Funeral service' },
    { value: 'viewing', label: 'Viewing' },
    { value: 'wake', label: 'Wake' },
    { value: 'graveside', label: 'Graveside' },
    { value: 'cremation', label: 'Cremation' },
    { value: 'burial', label: 'Burial' },
    { value: 'mortuary', label: 'Mortuary' },
  ];
  readonly radii = [
    { value: 2, label: '2 km' },
    { value: 5, label: '5 km' },
    { value: 10, label: '10 km' },
    { value: 25, label: '25 km' },
  ];
  readonly groupOptions = [
    { value: 'venue-hire', label: 'Halls, clubs & churches' },
    { value: 'food-drink', label: 'Restaurants, cafés & pubs' },
    { value: 'outdoors', label: 'Parks & outdoors' },
    { value: 'museums-history', label: 'Museums & history' },
    { value: 'disposal', label: 'Cemeteries & crematoria' },
    { value: 'mortuary', label: 'Mortuaries & funeral homes' },
  ];
  readonly typeLabel: Record<string, string> = { venue: 'Venue', cremation: 'Crematorium', burial: 'Cemetery', mortuary: 'Mortuary' };

  centre: { lat: number; lng: number; label: string } = SYDNEY;
  purpose = 'all';
  radiusKm = 10;
  more = false;
  groups: string[] = [];
  result: Nearby | null = null;
  loading = false;
  error = '';
  selected: string | null = null;
  moved = false;
  suburbQuery: SuburbHit | string | null = null;
  suburbHits: SuburbHit[] = [];

  private map: SimpleMap | null = null;
  readonly googleRefusedNote = GOOGLE_REFUSED_NOTE;
  get mapKind(): 'google' | 'maplibre' | null {
    return this.map?.kind ?? null;
  }
  private mapReady: Promise<void> | null = null;
  private autocomplete: any = null;
  private pacEl: Element | null = null;
  private destroyed = false;
  private requestNo = 0;

  constructor(
    private api: LocationsService,
    private config: DynamicDialogConfig,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  async ngAfterViewInit(): Promise<void> {
    const data: VenueMapData = this.config.data ?? {};
    if (data.purpose && this.purposes.some((p) => p.value === data.purpose)) this.purpose = data.purpose;
    if (data.centre) this.centre = { lat: data.centre.lat, lng: data.centre.lng, label: data.centre.label ?? 'the venue' };
    else if (data.suburb?.name && data.suburb.state) {
      try {
        const c = await this.api.centre(data.suburb.name, data.suburb.state, data.suburb.postcode).toPromise();
        if (c) this.centre = { lat: c.lat, lng: c.lng, label: `${data.suburb.name} ${data.suburb.state}` };
        else this.error = `${data.suburb.name} ${data.suburb.state} isn't a suburb we know: showing Sydney. Search for the suburb.`;
      } catch {
        this.error = 'Couldn\'t find the suburb: showing Sydney.';
      }
    }
    this.setupGoogleRefine();
    this.search();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.autocomplete && typeof google !== 'undefined') google.maps.event.clearInstanceListeners(this.autocomplete);
    this.pacEl?.remove();
    this.map?.destroy();
  }

  private refresh(): void {
    if (!this.destroyed) this.cdr.detectChanges();
  }

  get googleAvailable(): boolean {
    return typeof google !== 'undefined' && !!google?.maps?.places?.Autocomplete && !(window as any).__gmAuthFailed;
  }

  /** The list, built once per answer (a getter here made Angular rebuild every card on every change detection). */
  cards: Card[] = [];
  readonly cardKey = (_: number, c: Card) => c.key;
  readonly unplacedKey = (_: number, u: NearbyUnplaced) => `${u.serviceId}-${u.serviceMetaId}`;

  private buildCards(r: Nearby | null): Card[] {
    if (!r) return [];
    return [
      ...r.providers.map((item, i) => ({ kind: 'provider' as const, key: `s${item.serviceId}-${item.serviceMetaId}`, item, rank: i + 1 })),
      ...r.handTyped.map((item) => ({ kind: 'hand' as const, key: `h${item.locationId}`, item })),
      ...r.places.map((item) => ({ kind: 'place' as const, key: `p${item.locationId}`, item })),
    ];
  }

  // ------------------------------------------------------------------ searching

  search(): void {
    const n = ++this.requestNo;
    this.loading = true;
    this.moved = false;
    this.refresh();
    this.api.nearby({ lat: this.centre.lat, lng: this.centre.lng, radiusKm: this.radiusKm, purpose: this.purpose, more: this.more, groups: this.more ? this.groups : [] }).subscribe({
      next: (r) =>
        this.zone.run(() => {
          if (n !== this.requestNo) return;
          this.result = r;
          this.cards = this.buildCards(r);
          if (this.more && !this.groups.length) this.groups = [...r.groups];
          this.loading = false;
          this.error = '';
          this.refresh();
          this.drawMap(true);
        }),
      error: (e) =>
        this.zone.run(() => {
          if (n !== this.requestNo) return;
          this.loading = false;
          const err = e instanceof HttpErrorResponse ? e.error?.error : null;
          this.error = (err?.html || err?.message || [e?.message || 'something went wrong']).join(' ');
          this.refresh();
        }),
    });
  }

  setPurpose(p: string): void {
    this.purpose = p;
    this.groups = [];
    this.search();
  }

  setRadius(km: number): void {
    this.radiusKm = km;
    this.search();
  }

  toggleMore(): void {
    this.more = !this.more;
    this.search();
  }

  toggleGroup(g: string): void {
    this.groups = this.groups.includes(g) ? this.groups.filter((x) => x !== g) : [...this.groups, g];
    if (this.groups.length) this.search();
    else this.refresh();
  }

  findSuburbs(e: { query: string }): void {
    this.api.suburbs(e.query).subscribe({
      next: (hits) => this.zone.run(() => ((this.suburbHits = hits), this.refresh())),
      error: () => this.zone.run(() => ((this.suburbHits = []), this.refresh())),
    });
  }

  pickSuburb(hit: SuburbHit): void {
    this.centre = { lat: hit.lat, lng: hit.lng, label: `${hit.name} ${hit.state}` };
    this.search();
  }

  searchHere(): void {
    const c = this.map?.center();
    if (!c) return;
    this.centre = { lat: c.lat, lng: c.lng, label: 'this area' };
    this.search();
  }

  /** Google's place search, where it's available: refines the centre, and nothing from it is kept. */
  private setupGoogleRefine(): void {
    const input = this.googleInput?.nativeElement;
    if (!input || !this.googleAvailable) return;
    this.zone.runOutsideAngular(() => {
      const before = new Set(Array.from(document.querySelectorAll('.pac-container')));
      this.autocomplete = new google.maps.places.Autocomplete(input, { fields: ['geometry', 'name'], componentRestrictions: { country: 'au' } });
      this.pacEl = Array.from(document.querySelectorAll('.pac-container')).find((el) => !before.has(el)) ?? null;
      this.autocomplete.addListener('place_changed', () => {
        const p = this.autocomplete.getPlace();
        const loc = p?.geometry?.location;
        if (!loc) return;
        this.zone.run(() => {
          this.centre = { lat: loc.lat(), lng: loc.lng(), label: p.name || 'the place' };
          input.value = '';
          this.search();
        });
      });
    });
  }

  // ------------------------------------------------------------------ the map

  private async ensureMap(): Promise<void> {
    if (!this.mapEl) return;
    if (!this.mapReady) {
      this.map = new SimpleMap(this.mapEl.nativeElement);
      this.mapReady = this.zone.runOutsideAngular(() => this.map!.init(this.centre, 13));
      this.map.onMoved(() => this.zone.run(() => ((this.moved = true), this.refresh())));
    }
    await this.mapReady;
  }

  private async drawMap(fit: boolean): Promise<void> {
    await this.ensureMap();
    const r = this.result;
    if (!this.map || !r) return;
    const pins: MapPin[] = [{ id: 'centre', lat: r.centre.lat, lng: r.centre.lng, label: '', title: `Centre: ${this.centre.label}`, colour: '#64748b', small: true }];
    for (const c of this.cards) {
      if (c.kind === 'provider') pins.push({ id: c.key, lat: c.item.lat, lng: c.item.lng, label: String(c.rank), title: `${c.rank}. ${c.item.name}: used ${c.item.usage.countAll}×`, colour: '#16a34a' });
      else if (c.kind === 'hand') pins.push({ id: c.key, lat: c.item.lat, lng: c.item.lng, label: 'H', title: `${c.item.name ?? '(unnamed)'}: typed in ${c.item.countAll}×`, colour: '#d97706' });
      else pins.push({ id: c.key, lat: c.item.lat, lng: c.item.lng, label: c.item.curation === 'candidate' ? '★' : '', title: `${c.item.name ?? '(unnamed)'} (${this.kindLabel(c.item.kind)})${c.item.curation === 'candidate' ? ': a candidate' : ''}`, colour: c.item.curation === 'candidate' ? '#d97706' : '#7c3aed', small: c.item.curation !== 'candidate' });
    }
    this.map.setPins(pins, (id) => this.zone.run(() => this.select(id, false)));
    if (fit) {
      const near = pins.filter((p) => p.id !== 'centre').slice(0, 40);
      this.map.fitTo(near.length ? [pins[0], ...near] : [pins[0]]);
    }
  }

  select(key: string, pan = true): void {
    this.selected = key;
    this.refresh();
    document.getElementById(`venue-card-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    if (pan) {
      const c = this.cards.find((x) => x.key === key);
      if (c) this.map?.panTo({ lat: c.item.lat, lng: c.item.lng }, 16);
    }
  }

  // ------------------------------------------------------------------ text

  kindLabel(kind: string): string {
    return kind.replace(/_/g, ' ').replace(/\brsl\b/, 'RSL');
  }

  distanceText(m: number): string {
    return m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`;
  }

  usageText(u: { count12m: number; countAll: number; lastUsedAt: string | null }): string {
    if (!u.countAll) return 'Not used yet';
    const last = u.lastUsedAt ? `, last ${new Date(u.lastUsedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}` : '';
    return `Used ${u.count12m}× in the last 12 months, ${u.countAll}× in all${last}`;
  }

  dateText(v: string | null): string {
    return v ? new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'never';
  }

  addressText(u: NearbyUnplaced | NearbyProvider): string {
    const a = u.address;
    return [a.street, a.suburb, a.state].filter(Boolean).join(', ');
  }
}
